import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Download,
  TrendingUp,
  Users,
  DollarSign,
  PieChart,
  BarChart,
  Calendar,
  Target
} from 'lucide-react';
import { PropBotConnectedAccount } from '@/hooks/use-prop-bot-detail-admin';
import { CapitalDistributionChart } from './CapitalDistributionChart';
import { UserEngagementChart } from './UserEngagementChart';
import { PerformanceComparisonChart } from './PerformanceComparisonChart';
import { UsageTrendsChart } from './UsageTrendsChart';
import { AnalyticsExportTools } from './AnalyticsExportTools';

interface PropBotAnalyticsDashboardProps {
  botId: string;
  botName: string;
  connectedAccounts: PropBotConnectedAccount[];
  isLoading: boolean;
}

export const PropBotAnalyticsDashboard: React.FC<PropBotAnalyticsDashboardProps> = ({
  botId,
  botName,
  connectedAccounts,
  isLoading
}) => {
  const [selectedTab, setSelectedTab] = useState('overview');

  // Analytics calculations
  const analytics = useMemo(() => {
    if (!connectedAccounts || connectedAccounts.length === 0) {
      return {
        totalCapital: 0,
        averageBalance: 0,
        capitalDistribution: [],
        accountTypeDistribution: [],
        positionTypeDistribution: [],
        brokerDistribution: [],
        topUsers: [],
        growthRate: 0,
        retentionRate: 0,
        activeUsersLast30Days: 0
      };
    }

    const totalCapital = connectedAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const averageBalance = totalCapital / connectedAccounts.length;

    // Capital distribution by account type
    const liveCapital = connectedAccounts
      .filter(acc => acc.isLive)
      .reduce((sum, acc) => sum + (acc.balance || 0), 0);
    const demoCapital = totalCapital - liveCapital;

    // Account type distribution
    const liveAccounts = connectedAccounts.filter(acc => acc.isLive).length;
    const demoAccounts = connectedAccounts.length - liveAccounts;

    // Position type distribution
    const normalPositions = connectedAccounts.filter(acc => !acc.isReversePosition).length;
    const reversePositions = connectedAccounts.filter(acc => acc.isReversePosition).length;

    // Broker distribution
    const brokerStats = connectedAccounts.reduce((acc, account) => {
      const broker = account.broker || 'Unknown';
      acc[broker] = (acc[broker] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Top users by capital
    const userCapital = connectedAccounts.reduce((acc, account) => {
      const userId = account.userId;
      if (!acc[userId]) {
        acc[userId] = {
          userId,
          userName: account.userName,
          userEmail: account.userEmail,
          totalCapital: 0,
          accountCount: 0
        };
      }
      acc[userId].totalCapital += account.balance || 0;
      acc[userId].accountCount++;
      return acc;
    }, {} as Record<string, any>);

    const topUsers = Object.values(userCapital)
      .sort((a, b) => b.totalCapital - a.totalCapital)
      .slice(0, 10);

    // Calculate growth metrics (mock for now - would need historical data)
    const growthRate = 12.5; // Mock 12.5% growth
    const retentionRate = 85.3; // Mock 85.3% retention
    const activeUsersLast30Days = new Set(connectedAccounts.map(acc => acc.userId)).size;

    return {
      totalCapital,
      averageBalance,
      capitalDistribution: [
        { name: 'Live Accounts', value: liveCapital, count: liveAccounts },
        { name: 'Demo Accounts', value: demoCapital, count: demoAccounts }
      ],
      accountTypeDistribution: [
        { name: 'Live', value: liveAccounts },
        { name: 'Demo', value: demoAccounts }
      ],
      positionTypeDistribution: [
        { name: 'Normal', value: normalPositions },
        { name: 'Reverse', value: reversePositions }
      ],
      brokerDistribution: Object.entries(brokerStats).map(([name, value]) => ({
        name,
        value
      })),
      topUsers,
      growthRate,
      retentionRate,
      activeUsersLast30Days
    };
  }, [connectedAccounts]);

  // Summary metrics
  const summaryMetrics = [
    {
      title: 'Total Capital',
      value: `$${analytics.totalCapital.toLocaleString()}`,
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Average Balance',
      value: `$${analytics.averageBalance.toLocaleString()}`,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Active Users',
      value: analytics.activeUsersLast30Days.toString(),
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Retention Rate',
      value: `${analytics.retentionRate}%`,
      icon: Target,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryMetrics.map((metric, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                  <p className="text-2xl font-bold">{metric.value}</p>
                </div>
                <div className={`p-3 rounded-full ${metric.bgColor}`}>
                  <metric.icon className={`h-6 w-6 ${metric.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Analytics Dashboard */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <BarChart className="h-5 w-5" />
              Analytics Dashboard
            </CardTitle>
            <AnalyticsExportTools
              botId={botId}
              botName={botName}
              analytics={analytics}
              connectedAccounts={connectedAccounts}
            />
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <PieChart className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="performance" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Performance
              </TabsTrigger>
              <TabsTrigger value="engagement" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Engagement
              </TabsTrigger>
              <TabsTrigger value="trends" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Trends
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CapitalDistributionChart 
                  data={analytics.capitalDistribution}
                  isLoading={isLoading}
                />
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Position Type Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CapitalDistributionChart 
                      data={analytics.positionTypeDistribution}
                      isLoading={isLoading}
                      colors={['hsl(var(--primary))', 'hsl(var(--secondary))']}
                      title="Position Types"
                    />
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="performance" className="mt-6">
              <div className="grid grid-cols-1 gap-6">
                <PerformanceComparisonChart 
                  connectedAccounts={connectedAccounts}
                  isLoading={isLoading}
                />
              </div>
            </TabsContent>

            <TabsContent value="engagement" className="mt-6">
              <div className="grid grid-cols-1 gap-6">
                <UserEngagementChart 
                  connectedAccounts={connectedAccounts}
                  isLoading={isLoading}
                />
              </div>
            </TabsContent>

            <TabsContent value="trends" className="mt-6">
              <div className="grid grid-cols-1 gap-6">
                <UsageTrendsChart 
                  connectedAccounts={connectedAccounts}
                  isLoading={isLoading}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};